import { getAutomarkerTargetTemplate } from '@dyl/apiClients/backendApi';
import ChipList from '@dyl/components/ChipList';
import { useAutomarkerTargetTemplates } from '@dyl/systems/AmttManager';
import createSnackbarAction from '@dyl/utils/createSnackbarAction';
import {
    Button,
    CircularProgress,
    Dialog, DialogActions, DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    MenuItem, TextField,
    Typography
} from '@mui/material';
import { startCase, upperFirst } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import useChangeEvent from "@dyl/hooks/useChangeEvent";


export default function AddFromTemplateModal({ onAdd }) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const {
        templates,
        templatesStatus,
        refreshTemplates,
    } = useAutomarkerTargetTemplates();

    const [template, setTemplate] = useState({});
    const importTemplate = useCallback(async templateId => {
        const loadingIndicator = enqueueSnackbar('Loading template...', {
            variant: 'info',
            persist: true,
            action: createSnackbarAction({
                iconComponent: () => <CircularProgress size='1em' color='inherit'/>,
            }),
        });

        const template = await (async () => {
            try {
                return await getAutomarkerTargetTemplate(templateId);
            } catch (error) {
                enqueueSnackbar('Failed to load template');
                throw error;
            } finally {
                closeSnackbar(loadingIndicator);
            }
        })();

        setTemplate(template);
    });
    const onTemplateChange = useChangeEvent(importTemplate);


    const [isOpen, setOpen] = useState(false);
    useEffect(() => {
        if (isOpen) setTemplate({});
    }, [isOpen]);

    return (
        <>
            <Button onClick={() => setOpen(true)}>
                Add from Template
            </Button>

            <Dialog
                open={isOpen}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth='sm'
            >

                <DialogTitle>
                    Add from Template
                </DialogTitle>

                <DialogContent>
                    <Grid container rowSpacing={2} style={{ minHeight: '280px' }}>
                        <Grid item md={12}>
                            <TextField
                                label={'Target template'}
                                select
                                fullWidth
                                value={template?.target_template_id}
                                onChange={onTemplateChange}
                                variant={'filled'}
                                style={{ width: '280px', justifyContent: 'space-between' }}
                            >
                                {templatesStatus === 'complete' ? (
                                    templates.map(({ target_template_id, name }) => (
                                        <MenuItem key={target_template_id} value={target_template_id}>
                                            {name}
                                        </MenuItem>
                                    ))
                                ) : templatesStatus === 'error' ? (
                                    <MenuItem onClick={refreshTemplates}>
                                        Failed to load - click to retry
                                    </MenuItem>
                                ) : (
                                    <MenuItem style={{ justifyContent: 'space-between' }}>
                                        <CircularProgress />
                                    </MenuItem>
                                )}
                            </TextField>

                        </Grid>
                        {['primary_targets', 'secondary_targets'].map(key => !!template[key] && (
                            <Grid item md={12} key={key}>
                                <Typography variant='body'>
                                    {upperFirst(startCase(key).toLowerCase())} in this template
                                </Typography>

                                <ChipList
                                    value={template[key]}
                                    targetDictionary={{}}
                                    readOnly disabled
                                    disabledColor={'chip'}/>
                            </Grid>
                        ))}
                        <Grid item md={12}>
                            { !!template?.name && <FormHelperText>
                                Note: Changing a template later *won't* affect the imported targets.
                            </FormHelperText>
                            }
                        </Grid>

                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant='contained' color='primary' onClick={() => { setOpen(false); onAdd(template);}}>
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}